'use client';

/**
 * ActionPoints — top-of-SITREP card surfacing cross-domain
 * recommendations from lib/actionPoints.
 *
 * Shows up to 5 ranked points. Each card has:
 *   - severity dot (red flag / amber caution / cyan opportunity)
 *   - category chip (NUTRITION / TRAINING / RECOVERY / BODY / SYSTEM)
 *   - imperative title
 *   - rationale with actual numbers
 *   - evidence footer (sample count + effect size)
 *   - optional CTA when the rule has a deep link
 *
 * Empty state: when zero rules clear the confidence floor (cold-start
 * users or a user who genuinely has nothing to fix), shows a "no
 * action items" cleared-state message — NOT a fake placeholder. The
 * model is honest about lacking evidence.
 *
 * Loading state: small skeleton — the page already shows the SITREP
 * compiling spinner above the fold, so we don't double up.
 */

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { AlertTriangle, Zap, Heart, Apple, Activity, ArrowRight, CheckCircle2 } from 'lucide-react';
import type { ActionPoint, ActionPointCategory, ActionPointSeverity } from '@/lib/actionPoints';

interface Props {
  userId: string;
  /** Re-fetch trigger — bump when recovery/session sync events fire. */
  refreshKey?: number;
}

const SEVERITY_TW: Record<ActionPointSeverity, { dot: string; ring: string; chipTw: string; chipText: string }> = {
  flag:        { dot: 'bg-red-500',     ring: 'border-red-500/40',    chipTw: 'bg-red-500/15',    chipText: 'text-red-300' },
  caution:     { dot: 'bg-amber-400',   ring: 'border-amber-500/40',  chipTw: 'bg-amber-500/15',  chipText: 'text-amber-300' },
  opportunity: { dot: 'bg-cyan-400',    ring: 'border-cyan-500/40',   chipTw: 'bg-cyan-500/10',   chipText: 'text-cyan-300' },
};

const CATEGORY_LABEL: Record<ActionPointCategory, string> = {
  nutrition: 'NUTRITION',
  training: 'TRAINING',
  recovery: 'RECOVERY',
  'body-comp': 'BODY',
  integration: 'SYSTEM',
};

const CATEGORY_ICON: Record<ActionPointCategory, typeof Apple> = {
  nutrition: Apple,
  training: Zap,
  recovery: Heart,
  'body-comp': Activity,
  integration: AlertTriangle,
};

export default function ActionPoints({ userId, refreshKey = 0 }: Props) {
  const [points, setPoints] = useState<ActionPoint[] | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const { loadActionPoints } = await import('@/lib/actionPoints');
        const result = await loadActionPoints(userId);
        if (!cancelled) setPoints(result);
      } catch (e) {
        console.warn('[action-points] load failed:', e);
        if (!cancelled) setPoints([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, refreshKey]);

  if (!loaded) {
    return (
      <section className="relative rounded-xl border border-slate-800/80 bg-hud-ink/80 overflow-hidden">
        <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent" />
        <div className="px-4 py-4">
          <Header count={null} />
          <div className="mt-3 space-y-2">
            <div className="h-12 rounded-md bg-slate-900/60 border border-slate-800 animate-pulse" />
            <div className="h-12 rounded-md bg-slate-900/60 border border-slate-800 animate-pulse" />
          </div>
        </div>
      </section>
    );
  }

  // Cap at 5 — anything below the top 5 is noise vs the SITREP scroll length.
  const top = (points ?? []).slice(0, 5);

  return (
    <section className="relative rounded-xl border border-slate-800/80 bg-hud-ink/80 overflow-hidden">
      <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent" />
      <span aria-hidden className="pointer-events-none absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l border-amber-500/40" />
      <span aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r border-amber-500/40" />

      <div className="px-4 py-4">
        <Header count={top.length} />

        {top.length === 0 ? (
          <div className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/[0.04] px-3 py-3 flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-[12px] text-slate-200 leading-snug font-semibold">No action items right now.</p>
              <p className="text-[11px] text-slate-400 leading-snug mt-0.5">
                Either nothing's pulling you off course, or the engine doesn't yet have enough data to spot a cross-domain pattern. Keep logging — actionable points appear as soon as we have 10+ matched days.
              </p>
            </div>
          </div>
        ) : (
          <ul className="mt-3 space-y-2">
            {top.map(p => <Row key={p.id} point={p} />)}
          </ul>
        )}
      </div>
    </section>
  );
}

function Header({ count }: { count: number | null }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-0.5 h-3 bg-amber-400" aria-hidden />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] font-bold text-amber-400">
          Action Points
        </span>
        <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-600 hidden sm:inline">
          SYS·AP·01
        </span>
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 tabular-nums">
        {count === null ? '—' : count === 0 ? 'clear' : `${count} detected`}
      </div>
    </div>
  );
}

function Row({ point }: { point: ActionPoint }) {
  const tw = SEVERITY_TW[point.severity];
  const Icon = CATEGORY_ICON[point.category];
  const dim = point.confidence < 0.55;

  const inner = (
    <li className={`relative rounded-md border ${tw.ring} bg-slate-900/40 px-3 py-2.5 transition ${point.deepLink ? 'hover:bg-slate-900/60 cursor-pointer' : ''} ${dim ? 'opacity-80' : ''}`}>
      {/* Severity dot on the left edge */}
      <span aria-hidden className={`absolute top-3 left-0 -translate-x-1/2 w-2 h-2 rounded-full ${tw.dot}`} />
      <div className="flex items-start gap-2">
        <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${tw.chipText}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className={`font-mono text-[9px] uppercase tracking-[0.22em] font-bold px-1.5 py-0.5 rounded-sm ${tw.chipTw} ${tw.chipText}`}>
              {CATEGORY_LABEL[point.category]}
            </span>
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-600 tabular-nums shrink-0">
              {Math.round(point.confidence * 100)}% conf
            </span>
          </div>
          <p className="text-[13px] text-white font-semibold leading-snug">{point.title}</p>
          <p className="text-[11px] text-slate-400 leading-snug mt-0.5">{point.body}</p>
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-600 mt-1 tabular-nums">
            {point.evidence}
          </p>
        </div>
        {point.deepLink && (
          <ArrowRight className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
        )}
      </div>
    </li>
  );

  return point.deepLink ? (
    <Link href={point.deepLink} className="block">{inner}</Link>
  ) : (
    inner
  );
}
